import { memo } from 'react'
import clsx from 'clsx'
import { SxProps } from '@mui/material'
import { ENV, useDeviceType } from '@/utils'
import { Box } from '@/components/Layout'
import { withErrorBoundary } from '@/components/Utils'
import { APIProvider, Map as GoogleMap, InfoWindow } from '@vis.gl/react-google-maps'
import { mapStyles as styles } from './Map.style'
import { useMapController } from './Map.controller'
import { MapProps } from './Map.types'
import Cluster from './Cluster'
import ZoomControl from './ZoomControl'
import RouteLine from './RouteLine'

function Map({
  mapWrapperProps = {},
  infoWindowProps = {},
  showZoomControl = false,
  autoFitPinZoom = false,
  locations,
  circleRadiusInMiles,
  centerLocation,
  routePath,
}: MapProps) {
  const { isMobile, isDesktop } = useDeviceType()

  const { zoom, setZoom, selectedMarker, onMarkerClick, onMarkerClose, mapLocations } =
    useMapController({ locations })

  const {
    sx: mapWrapperSx,
    className: mapClassName = '',
    ...otherMapWrapperProps
  } = mapWrapperProps
  const { sx: infoWindowPropsSx, ...otherInfoWindowPropsProps } = infoWindowProps

  return (
    <Box
      sx={[styles.mapWrapper, mapWrapperSx] as SxProps}
      className={clsx('w-100 h-100', { [mapClassName]: Boolean(mapClassName) })}
      {...otherMapWrapperProps}>
      <APIProvider apiKey={ENV.GOOGLE_MAP_KEY}>
        <GoogleMap
          mapId={ENV.GOOGLE_MAP_ID}
          defaultCenter={centerLocation}
          zoom={zoom}
          disableDefaultUI
          clickableIcons={false}
          onClick={onMarkerClose}
          onZoomChanged={(ev) => setZoom(ev.detail.zoom)}>
          {mapLocations && mapLocations.length > 0 && (
            <Cluster
              locations={mapLocations}
              selectedMarker={selectedMarker}
              onMarkerClick={onMarkerClick}
              onMarkerClose={onMarkerClose}
              autoFitPinZoom={autoFitPinZoom}
              circleRadiusInMiles={circleRadiusInMiles}
              centerLocation={centerLocation}
            />
          )}

          {routePath && routePath.length > 1 && <RouteLine path={routePath} />}

          {showZoomControl && (
            <ZoomControl zoom={zoom} onZoomChange={(zoomValue: number) => setZoom(zoomValue)} />
          )}

          {isDesktop && selectedMarker && (
            <InfoWindow position={selectedMarker.location} headerDisabled pixelOffset={[0, -10]}>
              {selectedMarker.infoWindow}
            </InfoWindow>
          )}
        </GoogleMap>
      </APIProvider>

      {isMobile && selectedMarker && (
        <Box sx={[styles.infoWindow, infoWindowPropsSx] as SxProps} {...otherInfoWindowPropsProps}>
          {selectedMarker.infoWindow}
        </Box>
      )}
    </Box>
  )
}

export default memo(withErrorBoundary(Map))
